import { Component, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { environment } from '../environments/environment';
import { BillingPortalProps } from './models/BillingPortalProps';
import { InvoicesService } from './services/invoices.service';
import { BansStorageService } from './services/bans-storage.service';
import { ErrorPopupComponent } from './shared/popup/error-popup/error-popup.component';
import { ValidateTokenService } from './services/validate-token.service';
import { NavigationEnd, Router } from '@angular/router';
import { AuthenticationService } from './auth/authentication.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {
  @Input() token: string = 'initial token!';
  @ViewChild(ErrorPopupComponent) errorPopup!: ErrorPopupComponent;

  title = 'al-invoices';
  sidebarExpanded = false;
  banResponse: any;
  customerIDs: Array<string> = [];
  banCAs = new Map<string, Array<string>>();
  BillingPortalProps: BillingPortalProps = new BillingPortalProps();
  bansForTSA: any = [];
  instructionTextForPopup: string = "";
  serviceDownPageVisiblity: boolean = environment.serviceErrorPageVisibility;
  systemDownInstruction1: string = environment.systemDownInstructionHeader;
  systemDownInstruction2: string = environment.systemDownInstructionText;
  isTokenAvailable: boolean = false;
  isMobileView: boolean = false;

  constructor(
    private invoicesService: InvoicesService,
    private bansService: BansStorageService,
    private router: Router,
    private validateTokenService: ValidateTokenService,
    private authenticationService: AuthenticationService,
  ) {
    console.log("1st call");
    this.router.events.subscribe(async (ev) => {
      if (ev instanceof NavigationEnd) {
        console.log("App component loaded")
        this.validateTokenService.validateToken().subscribe(_ => {
          console.log("azure token is valid")
          this.authenticationService.fetchApigeeToken()
            .subscribe(res => {
              console.log("apigee token recived")
              if (!this.serviceDownPageVisiblity) {
                this.isTokenAvailable = true;
                console.log("Going to get bans")
                this.getBansFromBQ();
              }
            })
        });
      }
    })
  }

  ngOnInit() {
    this.bansService.setBanPresentFlag(true);
    this.checkDeviceView();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkDeviceView();
  }

  checkDeviceView() {
    const width = window.innerWidth;
    this.isMobileView = width <= 1200;
  }

  async getBansFromBQ() {
    this.bansService.setBanCallingStatus('FetchedBansFromBQ')
    this.BillingPortalProps = environment.billingProps;
    let fetchBanFlag = environment.fetchBanConfig
    const allBans: any[] = [];
    try {
      if(fetchBanFlag == "ban-inclusion"){
        if (this.BillingPortalProps.bans.filter(_ => _.length > 0).length == 0) this.getBansByCustomerId()
        else {
          const bansFromBQ = (await (await this.invoicesService.getBansFromBQ(this.BillingPortalProps.bans.filter(_ => _.length > 0).map(b => (b?.length !== 9) ? b : "0" + b))).toPromise()) || [];
          const bansFromCustomerId = (this.BillingPortalProps.customerIds.filter(_ => _.length > 0).length != 0) ? (await (await this.invoicesService.getBans(this.BillingPortalProps.customerIds.filter(_ => _.length > 0))).toPromise()) || [] : [];
          const bans = [...bansFromBQ, ...bansFromCustomerId]
          if (bans && bans.length > 0) {
            allBans.push(...bans);
            this.bansService.setBanPresentFlag(true);
            this.getBans(allBans);
          }
          else {
            this.getBansByCustomerId();
          }
        }
      }
      else if(fetchBanFlag == "vcid"){
        const bans = await (await this.invoicesService.getBANsMetadata(this.BillingPortalProps.customerIds, this.BillingPortalProps.bans)).toPromise();
        if (bans && bans.length > 0) {
          allBans.push(...bans);
        } else {
          console.warn(`No bans found for any of the provided customer IDs.`);
        }
        if (allBans.length > 0) {
          this.bansService.setBanPresentFlag(true);
          this.getBans(allBans);
        } else {
          this.getBansByCustomerId();
        }
      }
    }
    catch (error) {
      console.log('Error occured while fetching bans', error);
      this.getBansByCustomerId();
    }
  }

  async getBansByCustomerId() {
    this.bansService.setBanCallingStatus('FetchedBansByCustomerId')
    this.BillingPortalProps = environment.billingProps;
    this.customerIDs = this.BillingPortalProps ? this.BillingPortalProps.customerIds : [environment.customerID];
    if (environment.fallbackEnable && !this.customerIDs.includes(environment.customerID)) {
      this.customerIDs.push(environment.customerID);
    }
    const allBans: any[] = [];
    try {
      const bans = await (await this.invoicesService.getBans(this.customerIDs)).toPromise();
      if (bans && bans.length > 0) {
        allBans.push(...bans);
      } else {
        console.warn(`No bans found for any of the provided customer IDs.`);
      }
      if (allBans.length > 0) {
        this.bansService.setBanPresentFlag(true);
        this.getBans(allBans);
      } else {
        if (environment.fallbackEnable) {
          console.log("No bans found, fetching default customer data as fallback");
          await this.fetchFallbackBans();
        } else {
          console.log("No bans received from any customer ID");
          this.showErrorScreen();
          this.bansService.setBanPresentFlag(false);
        }
      }
    } catch (error) {
      this.showErrorScreen();
      this.bansService.setBanPresentFlag(false);
      console.error("Unexpected error occurred:", error);
    }
  }

  async fetchFallbackBans() {
    const fallbackCustomerIDs = [environment.customerID];
    const allBans: any[] = [];

    const fallbackBanPromises = fallbackCustomerIDs.map(async (customerId) => {
      try {
        const bans = await (await this.invoicesService.getBans([customerId])).toPromise();
        if (bans && bans.length > 0) {
          allBans.push(...bans);
        } else {
          console.log("No data found for fallback customer ID:", customerId);
        }
      } catch (error) {
        console.log(`Error fetching fallback bans for customer ID ${customerId}:`, error);
      }
    });

    await Promise.all(fallbackBanPromises);

    if (allBans.length === 0) {
      this.customerIDs = [];
      this.customerIDs.push(environment.customerID);
      (await Promise.all(this.customerIDs)).forEach(async (element: any) => {
        (await this.invoicesService.getBans(element))
          .subscribe((bans: any) => {
            this.banResponse = bans;
            this.bansService.setBanPresentFlag(true);
            this.getBans(bans);
          }, (err) => {
            console.log(err);
            this.showErrorScreen();
            this.bansService.setBanPresentFlag(false);
          });
      });
    } else {
      this.bansService.setBanPresentFlag(true);
      this.getBans(allBans);
    }
  }

  sleep(ms: any) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  getBans(bans: any) {
    this.bansService.setBansApiResponse(bans);
    bans.forEach((ban: { bancust: string | null; contractacc: string; vktyp: string }) => {
      if (ban.bancust) {
        let acc = this.banCAs.get(ban.bancust) || [];
        if (!acc.includes(ban.contractacc)) acc.push(ban.contractacc);
        this.banCAs.set(ban.bancust, acc);
      }
    });
    const bancustMap = bans.reduce((acc: any, item: any) => {
      if (!acc[item.bancust]) {
        acc[item.bancust] = {
          bancust: item.bancust,
          vktyp: [],
          contractacc: []
        };
      }
      acc[item.bancust].vktyp.push(item.vktyp);
      acc[item.bancust].contractacc.push({ vktyp: item.vktyp, contractacc: item.contractacc });
      return acc;
    }, {});
    for (const bancust in bancustMap) {
      const vktypArray = bancustMap[bancust].vktyp;
      const isSamevkTyp = vktypArray.every((vk: any) => vk === vktypArray[0]);
      const isTSA = vktypArray.some((vk: any) => environment.vktypForTSA.includes(vk));
      const filteredContractacc = isTSA
        ? bancustMap[bancust].contractacc.filter((item: any) => environment.vktypForTSA.includes(item.vktyp))
        : bancustMap[bancust].contractacc;
      const filteredVktyp = isTSA ? vktypArray.filter((vk: any) => environment.vktypForTSA.includes(vk)) : vktypArray;
      this.bansForTSA.push({
        bancust: bancust,
        vktyp: filteredVktyp.map((item: any) => item),
        contractacc: filteredContractacc.map((item: any) => item.contractacc),
        isSamevkTyp: isSamevkTyp,
        isTSA: isTSA
      });
    }
    this.bansService.setTSABans(this.bansForTSA);
    let ban = JSON.parse(JSON.stringify(localStorage.getItem("Selected_BAN_Dropdown_Value"))) || "";
    if (ban && !this.banCAs.has(ban)) {
      localStorage.removeItem('Selected_BAN_Dropdown_Value');
      localStorage.removeItem('Selected_BAN_CAs_Dropdown');
    }

    const sortMap = (map: Map<string, Array<string>>) => {
      return new Map(
        [...map.entries()].sort((a, b) => {
          const keyA = a[0];
          const keyB = b[0];
          if (!isNaN(Number(keyA)) && !isNaN(Number(keyB))) {
            return Number(keyA) - Number(keyB); // Numeric comparison for purely numeric keys
          }
          return keyA.localeCompare(keyB, undefined, { numeric: true, sensitivity: 'base' });
        })
      );
    };
    const sortedBanCAs = sortMap(this.banCAs);

    this.bansService.setBans(sortedBanCAs);
  }

  toggleSideNav(e: any) {
    if (e.target.className == "hamburger-icon") {
      this.sidebarExpanded = !this.sidebarExpanded
    }
    else {
      this.sidebarExpanded = false;
    }
  }

  showErrorScreen() {
    this.instructionTextForPopup = "There is an issue with your account; it may not have any associated BANs. Please contact the support team.";
    localStorage.removeItem('Selected_BAN_Dropdown_Value');
    localStorage.removeItem('Selected_BAN_CAs_Dropdown');
    this.errorPopup.showPopup();
  }
}
